import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import { useSnackbar } from "context/SnackbarContext";
import SearchBar from "common/Documents/SearchBar/SearchBar";
import FilterBar from "common/Documents/FilterBar/FilterBar";
import ActionButtons from "common/Documents/ActionButtons/ActionButtons";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { useRecognition } from "context/RecognitionContext";
import { useExport } from "context/ExportContext";
import Pagination from "@mui/material/Pagination";
import Loader from "components/Loader/Loader";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import InfoIcon from "@mui/icons-material/Info";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import documentTableData from "common/Documents/documents/documentTableData";
import Checkbox from "@mui/material/Checkbox";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import validateTotals, {
  checkTotalsMismatch,
  checkLineItemsMismatch,
} from "common/Documents/utils/validateTotals";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDButton from "components/MDButton";
import FormGroup from "@mui/material/FormGroup";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { getDocumentTypeMeta } from "common/utils/documentType";

import PdfExportDialog from "./components/PdfExportDialog";
import XmlExportSettingsDialog from "./components/XmlExportSettingsDialog";
import ExportVariantSelector, { isExportVariantValid } from "./components/ExportVariantSelector";
import UnmatchedLineItemsDialog from "./components/UnmatchedLineItemsDialog";
import DocumentDetailsModal from "./components/DocumentDetailsModal";
import ColumnManager from "./components/ColumnManager";
import BulkActionsBar from "common/Documents/ActionButtons/BulkActionsBar";

const expandableTableColumns = new Set([
  "title",
  "supplier_name",
  "receiver_name",
  "supplier_address",
  "supplier_iban",
  "notes",
]);

// On the sales tab the counterparty is the buyer: supplier columns show the buyer and vice versa.
// Column ids stay the same so hidden-column and width settings keep working.
const SALE_PARTY_COLUMNS = {
  supplier_name: { accessor: "receiver_name", header: "buyerName" },
  supplier_registration: { accessor: "receiver_registration", header: "buyerRegistration" },
  receiver_name: { accessor: "supplier_name", header: "supplierName" },
  receiver_registration: { accessor: "supplier_registration", header: "supplierRegistration" },
};

const fixedTableColumnWidths = {
  processing: 34,
  selection: 40,
  last_exported_at: 82,
  is_recognized: 50,
  document_type: 135,
  "data.is_reverse_charge_vat_96": 72,
  supplier_registration: 120,
  receiver_registration: 120,
  invoice_id: 140,
  invoice_date: 104,
  due_date: 104,
  net_amount: 100,
  total_tax_amount: 100,
  total_amount: 105,
  currency: 70,
  id: 96,
};

function DocumentPage() {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    suppliers: [],
    excludeSuppliers: false,
    documentTypes: [],
    excludeDocumentTypes: false,
    dateType: "invoice",
    startDate: null,
    endDate: null,
    xmlType: "",
    searchQuery: "",
    searchField: "all",
    exactMatch: false,
    recognitionStatus: "", // "", "recognized", or "unrecognized"
    advanceReport: "", // "", "yes", or "no"
    exportStatus: "",
  });
  const [pageSizeValue, setPageSizeValue] = useState(() => (isMdDown ? 50 : 100));
  const [sortBy, setSortBy] = useState([]);
  const [documentTab, setDocumentTab] = useState("unrecognized");
  const [tabCounts, setTabCounts] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [deleteCompanyId, setDeleteCompanyId] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [savedViewName, setSavedViewName] = useState("");
  const showSnackbar = useSnackbar();
  const { selectedCompany } = useCompany();
  const location = useLocation();
  const queryClient = useQueryClient();
  const hydratedCompanyIdRef = useRef(null);
  const [hydratedCompanyId, setHydratedCompanyId] = useState(null);
  const savedScrollPositionRef = useRef(null);
  const previousIsMdDownRef = useRef(isMdDown);

  // PDF tipo dialogo būsenos
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [documentType, setDocumentType] = useState("purchase"); // purchase or sales
  const [unitPriceInput, setUnitPriceInput] = useState(""); // Vieneto kaina

  // Upload UI state (so it's obvious when many files are uploading)
  const [uploadUi, setUploadUi] = useState({
    status: "idle", // idle | uploading | success | error
    progress: null, // 0-100
    totalFiles: 0,
    files: [],
    message: "",
    failedFiles: [],
  });
  const uploadAbortRef = useRef(null);

  useEffect(() => {
    if (uploadUi.status !== "success") return undefined;

    const timeoutId = window.setTimeout(() => {
      setUploadUi((current) =>
        current.status === "success" ? { ...current, status: "idle" } : current
      );
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [uploadUi.status, uploadUi.message]);

  const looksLikeHtml = (value) =>
    typeof value === "string" && /<\s*!doctype|<\s*html/i.test(value);

  const normalizeServerMessage = (value, fallback) => {
    if (!value) return fallback;
    if (looksLikeHtml(value)) return fallback;
    if (typeof value === "string") return value;
    return fallback;
  };

  const chunkArray = (arr, chunkSize) => {
    const out = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      out.push(arr.slice(i, i + chunkSize));
    }
    return out;
  };

  const { data: pendingPdfSplitJobs = [], isLoading: pendingPdfSplitsLoading } = useQuery({
    queryKey: ["pdf-split-jobs", selectedCompany?.id, "pending"],
    queryFn: async () => {
      const response = await api.get("/api/pdf-split-jobs/", {
        params: { company_id: selectedCompany.id, status: "pending" },
      });
      return response.data.jobs || [];
    },
    enabled: !!selectedCompany?.id,
    staleTime: 30000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Recognition options dialog state

  // Recognition state comes from global context (persists across navigation)
  const { batchState, processingIds, docOverlays, startBatch, cancelBatch, clearOverlays } =
    useRecognition();
  // batchState.isRunning / .cancelled etc. are shown in the global RecognitionProgressPanel

  // XML Export context (background export with download button in floating panel)
  const { startExport } = useExport();

  // XML Export settings dialog state
  const [xmlExportDialogOpen, setXmlExportDialogOpen] = useState(false);
  const [exportVariant, setExportVariant] = useState(null);
  const [exportSaving, setExportSaving] = useState(false);
  const [fileExport, setFileExport] = useState(null);

  const assignExportVariant = async (documentIds) => {
    if (!exportVariant) return;
    await api.post("/api/suppliers/assign-document-variant/", {
      company_id: selectedCompany.id,
      document_ids: documentIds,
      variant: exportVariant.variant,
    });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
  };
  const [xmlExportType, setXmlExportType] = useState("agnum");
  const [xmlExportWithMatches, setXmlExportWithMatches] = useState(false);
  const [selectedPozymiai, setSelectedPozymiai] = useState([]);
  const [includePvmInTotal, setIncludePvmInTotal] = useState(true); // M4 field
  const [spacyModelSize, setSpacyModelSize] = useState("lg"); // spaCy model size choice
  const [matcherType, setMatcherType] = useState("llm"); // 'spacy' or 'llm'
  const [overridePvmCode, setOverridePvmCode] = useState(""); // PVM code override
  const [recalculateVat, setRecalculateVat] = useState(false); // Recalculate VAT for zero-VAT docs
  const [isBusinessTrip, setIsBusinessTrip] = useState(false);
  const [businessTripDocumentNumber, setBusinessTripDocumentNumber] = useState("");
  const [pendingSupplierExport, setPendingSupplierExport] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("pendingSupplierExport") || "null");
    } catch (error) {
      return null;
    }
  });
  const [supplierPreflight, setSupplierPreflight] = useState({
    open: false,
    loading: false,
    items: [],
    exportParams: null,
  });
  const [matchReview, setMatchReview] = useState({
    open: false,
    loading: false,
    items: [],
    companyId: null,
    defaultWarehouseCode: "",
    defaultGroup: "Prekės",
    exportParams: null,
  });

  // Document details modal (mobile-friendly)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // PVM code options (same as in suppliers)
  const pvmCodeOptions = [
    { value: "", label: t("useSupplierPvmCode") },
    {
      value: "PVM1",
      label: "PVM1 - Šalies teritorijoje patiektos prekės ir / ar suteiktos paslaugos, 21%",
    },
    {
      value: "PVM2",
      label: "PVM2 - Šalies teritorijoje patiektos prekės ir / ar suteiktos paslaugos, 9%",
    },
    {
      value: "PVM3",
      label: "PVM3 - Šalies teritorijoje patiektos prekės ir / ar suteiktos paslaugos, 5%",
    },
    { value: "PVM25", label: "PVM25 - Pirkėjas išskaito ir sumoka PVM, 21%" },
    { value: "PVM26", label: "PVM26 - Pirkėjas išskaito ir sumoka PVM, 9%" },
    { value: "PVM27", label: "PVM27 - Pirkėjas išskaito ir sumoka PVM, 5%" },
    { value: "PVM5", label: "PVM5 - Neapmokestinamas PVM" },
    { value: "PVM6", label: "PVM6 - Privatiems poreikiams tenkinti, 21%" },
    { value: "PVM7", label: "PVM7 - Privatiems poreikiams tenkinti, 9%" },
    { value: "PVM8", label: "PVM8 - Privatiems poreikiams tenkinti, 5%" },
    { value: "PVM28", label: "PVM28 - Privatiems poreikiams tenkinti, 0%" },
    { value: "PVM9", label: "PVM9 - Ilgalaikio turto pasigaminimas ir pagerinimas, 21%" },
    { value: "PVM30", label: "PVM30 - Ilgalaikio turto pasigaminimas ir pagerinimas, 9%" },
    { value: "PVM31", label: "PVM31 - Ilgalaikio turto pasigaminimas ir pagerinimas, 5%" },
    { value: "PVM32", label: "PVM32 - Sandoriai su specialia schema (marža), 21%" },
    { value: "PVM33", label: "PVM33 - Sandoriai su specialia schema (marža), 0%" },
    { value: "PVM12", label: "PVM12 - Prekių eksportas, 0%" },
    { value: "PVM13", label: "PVM13 - ES PVM mokėtojams patiektos prekės, 0%" },
    { value: "PVM14", label: "PVM14 - Kiti sandoriai, 0%" },
    {
      value: "PVM15",
      label: "PVM15 - Už Lietuvos ribų patiektos prekės ir/ar paslaugos, neapmokestinama",
    },
    { value: "PVM16", label: "PVM16 - Iš kitų valstybių narių prekių įsigijimas, 21%" },
    { value: "PVM17", label: "PVM17 - Iš kitų valstybių narių prekių įsigijimas, 9%" },
    { value: "PVM18", label: "PVM18 - Iš kitų valstybių narių prekių įsigijimas, 5%" },
    { value: "PVM35", label: "PVM35 - Iš kitų valstybių narių prekių įsigijimas, 0%" },
    { value: "PVM19", label: "PVM19 - Trikampė prekyba, neapmokestinama" },
    { value: "PVM20", label: "PVM20 - Iš užsienio valstybių paslaugos, 21%" },
    { value: "PVM37", label: "PVM37 - Iš užsienio valstybių paslaugos, 5%" },
    { value: "PVM38", label: "PVM38 - Iš užsienio valstybių paslaugos, 0%" },
    { value: "PVM39", label: "PVM39 - Iš užsienio valstybių paslaugos, neapmokestinama" },
    { value: "PVM21", label: "PVM21 - Iš ES PVM mokėtojų paslaugos, 21%" },
    { value: "PVM40", label: "PVM40 - Iš ES PVM mokėtojų paslaugos, 5%" },
    { value: "PVM41", label: "PVM41 - Iš ES PVM mokėtojų paslaugos, 0%" },
    { value: "PVM42", label: "PVM42 - Iš ES PVM mokėtojų paslaugos, neapmokestinama" },
    { value: "PVM43", label: "PVM43 - Užsienio apmokestinamojo asmens prekės, 21%" },
    { value: "PVM44", label: "PVM44 - Užsienio apmokestinamojo asmens prekės, 9%" },
    { value: "PVM45", label: "PVM45 - Užsienio apmokestinamojo asmens prekės, 5%" },
    { value: "PVM46", label: "PVM46 - Užsienio apmokestinamojo asmens prekės, 0%" },
    { value: "PVM47", label: "PVM47 - Užsienio apmokestinamojo asmens prekės, neapmokestinama" },
    {
      value: "PVM48",
      label: "PVM48 - Už Lietuvos ribų įsigytos prekės ir/ar paslaugos, neapmokestinama",
    },
    { value: "PVM49", label: "PVM49 - Žemės ūkio produkcija iš ūkininkų, 6%" },
    { value: "PVM100", label: "PVM100 - Kiti atvejai" },
    { value: "PVM50", label: "PVM50 - ES PVM mokėtojams tiekiamos prekės, 0%" },
    { value: "PVM51", label: "PVM51 - PVM taikomi sandoriai, 0%" },
    { value: "PVM52", label: "PVM52 - Sandoriai su specialia schema (marža), 9%" },
    { value: "PVM53", label: "PVM53 - Sandoriai su specialia schema (marža), 5%" },
    { value: "PVM54", label: "PVM54 - Iš užsienio paslaugos, 9%" },
    { value: "PVM55", label: "PVM55 - Paramos gavėjams skirtos prekės ir/ar paslaugos, 0%" },
    {
      value: "PVM56",
      label: "PVM56 - Paramos gavėjams iš ES valstybės narės įsigytos prekės, neapmokestinama",
    },
    { value: "PVM57", label: "PVM57 - Iš ES PVM mokėtojų paslaugos, 9%" },
    { value: "NE_ISAF", label: "NE_ISAF - Nepatenka į I.SAF ataskaitą" },
  ];

  // Funkcija atidaryti PDF tipo pasirinkimo dialogą
  const openPdfTypeDialog = () => {
    if (!selectedCompany) {
      showSnackbar(t("selectCompanyFirst"), "error");
      return;
    }

    if (!hasActiveFilters()) {
      showSnackbar(t("applyFilters"), "warning");
      return;
    }

    setPdfDialogOpen(true);
  };

  // PDF tipo dialogo uždarymas
  const handlePdfDialogClose = () => {
    setPdfDialogOpen(false);
    setUnitPriceInput(""); // Reset unit price when closing dialog
  };

  // PDF tipo pasirinkimas ir generavimas
  const handlePdfTypeSelect = () => {
    // Validate unit price if provided
    if (unitPriceInput && (isNaN(parseFloat(unitPriceInput)) || parseFloat(unitPriceInput) <= 0)) {
      showSnackbar("Please enter a valid unit price", "error");
      return;
    }

    setPdfDialogOpen(false);
    generatePdfMutation.mutate({
      documentType,
      unitPrice: unitPriceInput ? parseFloat(unitPriceInput) : null,
    });
  };

  // Function to check if at least one filter is active
  const hasActiveFilters = () => {
    return (
      filters.suppliers.length > 0 ||
      (Array.isArray(filters.documentTypes) && filters.documentTypes.length > 0) ||
      filters.startDate !== null ||
      filters.endDate !== null ||
      filters.xmlType !== "" ||
      filters.advanceReport !== "" ||
      filters.searchQuery !== ""
    );
  };

  // Handle search
  const handleSearch = (searchData) => {
    // Handle both old string format and new object format for backward compatibility
    if (typeof searchData === "string") {
      setFilters((prev) => ({
        ...prev,
        searchQuery: searchData,
        searchField: "all",
        exactMatch: false,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        searchQuery: searchData.query,
        searchField: searchData.field,
        exactMatch: searchData.exactMatch || false,
      }));
    }
    setPageIndex(0); // Reset to first page when searching
  };

  // Dokumentų krovimas su paginacija tik tada, kai yra filtrai
  const {
    data: documentPageData,
    isLoading: documentsLoading,
    error: documentsError,
  } = useQuery({
    queryKey: [
      "documents",
      selectedCompany?.id,
      filters,
      documentTab,
      pageIndex,
      pageSizeValue,
      sortBy,
    ],
    queryFn: async () => {
      const params = {
        company_id: selectedCompany?.id,
        suppliers:
          filters.suppliers.length > 0
            ? filters.suppliers.map((s) => s.supplier_registration).join(",")
            : undefined,
        exclude_suppliers: filters.excludeSuppliers,
        date_type: filters.dateType || "invoice",
        start_date: filters.startDate ? dayjs(filters.startDate).format("YYYY-MM-DD") : undefined,
        end_date: filters.endDate ? dayjs(filters.endDate).format("YYYY-MM-DD") : undefined,
        xml_type: filters.xmlType || undefined,
        search: filters.searchQuery || undefined,
        search_field: filters.searchField || "all",
        exact_match: filters.exactMatch || false,
        document_tab: documentTab,
        export_status: filters.exportStatus || undefined,
        advance_report: filters.advanceReport || undefined,
        page: pageIndex + 1, // Backend uses 1-indexed pages
        page_size: pageSizeValue,
        sort_by:
          (documentTab === "sale" && SALE_PARTY_COLUMNS[sortBy[0]?.id]?.accessor) ||
          sortBy[0]?.id ||
          undefined,
        sort_desc: sortBy[0]?.desc || false,
      };

      console.log("Fetching documents with params:", params);

      try {
        const response = await api.get(`/api/documents/`, { params });
        console.log("API Response:", {
          totalCount: response.data.total_count,
          totalPages: response.data.total_pages,
          currentPage: response.data.current_page,
          documentsCount: response.data.documents?.length || 0,
        });

        return response.data;
      } catch (error) {
        console.error("API Error:", error);
        throw error;
      }
    },
    enabled: !!selectedCompany?.id && hydratedCompanyId === selectedCompany.id,
    // Keep an already loaded list while the user opens and returns from a document.
    // Explicit mutations invalidate this query, so edited data still refreshes immediately.
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const documents = useMemo(
    () =>
      (documentPageData?.documents || []).map((doc) => {
        const { id: dataId, ...dataWithoutId } = doc.data || {};
        const totalsMismatch = checkTotalsMismatch(doc.data);
        const lineItemsMismatch = checkLineItemsMismatch({
          ...doc.data,
          line_items: doc.line_items,
        });
        const hasMismatch = totalsMismatch || lineItemsMismatch;
        return {
          ...doc,
          ...dataWithoutId,
          is_recognized: doc.is_recognized === true,
          totalsMismatch,
          lineItemsMismatch,
          isValid:
            doc.is_recognized === true && doc.data
              ? !hasMismatch && validateTotals({ ...doc.data, line_items: doc.line_items })
              : true,
        };
      }),
    [documentPageData?.documents]
  );
  useEffect(() => {
    if (!documentPageData) return;
    setTotalCount(documentPageData.total_count || 0);
    setTotalPages(documentPageData.total_pages || 1);
    setTabCounts(documentPageData.tab_counts || {});
  }, [documentPageData]);

  useEffect(() => {
    if (selectedCompany?.id && batchState.successCount > 0) {
      queryClient.invalidateQueries({ queryKey: ["documents", selectedCompany.id] });
    }
  }, [batchState.successCount, selectedCompany?.id, queryClient]);

  const recognizeMutation = useMutation({
    mutationFn: async ({ document_ids, use_company_code, use_markdown }) => {
      console.log(
        "Recognizing with IDs:",
        document_ids,
        "Use company code:",
        use_company_code,
        "Use markdown:",
        use_markdown
      );
      const response = await api.post(`/api/recognize/`, {
        document_ids,
        use_company_code: use_company_code,
        use_markdown: use_markdown,
      });
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Recognition success:", data);
      queryClient.invalidateQueries(["documents", selectedCompany?.id, filters]);
      queryClient.invalidateQueries(["pdf-split-jobs", selectedCompany?.id, "pending"]);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));
      showSnackbar(data.message || t("recognitionCompleted"), "success");
      toggleAllRowsSelected(false);
    },
    onError: (error) => {
      console.error("Recognition error:", error.response?.data || error.message);
      // 504 from nginx or gateway: show localized timeout info
      if (error.response?.status === 504) {
        showSnackbar(
          t("errorRecognizingDocument") +
            (t("gatewayTimeoutSuffix") || ": serverio laiko limitas (504)"),
          "error"
        );
        return;
      }
      if (error.response?.status === 400) {
        if (
          error.response?.data?.message?.includes("No LLM settings configured") ||
          error.response?.data?.message?.includes("No model version specified")
        ) {
          showSnackbar(
            "Please configure LLM settings with valid model versions before recognizing documents.",
            "error"
          );
        } else if (error.response?.data?.message?.includes("Invalid")) {
          showSnackbar(
            "Invalid model version in LLM settings. Please update your model version in the settings page.",
            "error"
          );
        } else {
          showSnackbar(error.response?.data?.message || t("errorRecognizingDocument"), "error");
        }
      } else {
        showSnackbar(error.response?.data?.message || t("errorRecognizingDocument"), "error");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (document_ids) => {
      console.log("Deleting document IDs:", document_ids);
      const response = await api.post(`/api/documents/delete-multiple/`, { document_ids });
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Delete success:", data);
      queryClient.invalidateQueries(["documents", selectedCompany?.id, filters]);
      queryClient.invalidateQueries(["pdf-split-jobs", selectedCompany?.id, "pending"]);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));

      const deletedCount = Number(data.deleted_count);
      const message =
        Number.isInteger(deletedCount) && deletedCount >= 0
          ? (t("documentsDeletedCount") || "{count} documents have been deleted.").replace(
              "{count}",
              deletedCount
            )
          : data.message || t("documentsDeletedSuccessfully");
      showSnackbar(message, "success");
      toggleAllRowsSelected(false);
      setDeleteConfirmOpen(false);
      setDocumentsToDelete([]);
      setDeleteCompanyId(null);
    },
    onError: (error) => {
      console.error("Delete error:", error.response?.data || error.message);
      showSnackbar(error.response?.data?.message || "Failed to delete documents", "error");
    },
  });

  const exportExcelMutation = useMutation({
    mutationFn: async ({ document_ids, use_matches, spacy_model_size }) => {
      const response = await api.post(
        "/api/export_excel/",
        {
          document_ids,
          use_matches,
          spacy_model_size: spacy_model_size || "lg",
        },
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      // Build filename identical to XML export: use actual invoice_date range of exported documents
      const selectedIds =
        selectedFlatRows.length > 0
          ? selectedFlatRows.map((row) => row.original.id)
          : (documents || []).map((doc) => doc.id);

      const selectedDocs = (documents || []).filter((d) => selectedIds.includes(d.id));
      const invoiceDates = selectedDocs
        .map((d) => d.invoice_date)
        .filter(Boolean)
        .map((d) => dayjs(d))
        .filter((d) => d.isValid());

      let filename;
      if (invoiceDates.length > 0) {
        const minObj = invoiceDates.reduce((acc, d) => (acc && acc.isBefore(d) ? acc : d));
        const maxObj = invoiceDates.reduce((acc, d) => (acc && acc.isAfter(d) ? acc : d));
        const minDate = minObj.format("YYYY-MM-DD");
        const maxDate = maxObj.format("YYYY-MM-DD");
        // Always use _to_ pattern to match XML naming, even if equal
        filename = `documents_${minDate}_to_${maxDate}.xlsx`;
      } else {
        // Match XML fallback when no invoice dates are present
        filename = `documents_unknown_start_to_unknown_end.xlsx`;
      }

      // Create download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      queryClient.invalidateQueries({ queryKey: ["documents", selectedCompany?.id] });
      showSnackbar("Excel failas sėkmingai eksportuotas", "success");
    },
    onError: (error) => {
      console.error("Export Excel error:", error);
      const errorMessage = error.response?.data?.error || error.message;
      showSnackbar(`Failed to export Excel: ${errorMessage}`, "error");
    },
  });

  // RODA TXT export mutation
  const exportRodaMutation = useMutation({
    mutationFn: async ({ document_ids, include_unit }) => {
      const response = await api.post(
        "/api/export_roda_txt/",
        { document_ids, include_unit: include_unit !== false },
        { responseType: "blob" }
      );
      return response;
    },
    onSuccess: (response) => {
      // Build filename from backend headers if present, otherwise fallback
      const disposition =
        response.headers?.["content-disposition"] || response.headers?.get?.("content-disposition");
      let filename = "roda_eilutes.txt";
      if (disposition) {
        const m = /filename="?([^";]+)"?/i.exec(disposition);
        if (m && m[1]) filename = m[1];
      }

      const contentType = response.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      queryClient.invalidateQueries({ queryKey: ["documents", selectedCompany?.id] });
      showSnackbar("RODA TXT sėkmingai eksportuotas", "success");
    },
    onError: (error) => {
      console.error("Export RODA error:", error);
      const errorMessage = error.response?.data?.error || error.message;
      showSnackbar(`Failed to export RODA TXT: ${errorMessage}`, "error");
    },
  });

  const handleExportRoda = () => {
    if (selectedFlatRows.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }
    setExportVariant(null);
    setFileExport({ type: "roda", documentIds: selectedFlatRows.map((row) => row.original.id) });
  };

  const generatePdfMutation = useMutation({
    mutationFn: async ({ documentType, unitPrice }) => {
      if (!selectedCompany?.id) {
        throw new Error("Company not selected"); // Prevent API call if no company
      }

      // Construct params from current filters
      const params = {
        company_id: selectedCompany.id,
        suppliers:
          filters.suppliers.length > 0
            ? filters.suppliers.map((s) => s.supplier_registration).join(",")
            : undefined,
        date_type: filters.dateType || "invoice",
        start_date: filters.startDate ? dayjs(filters.startDate).format("YYYY-MM-DD") : undefined,
        end_date: filters.endDate ? dayjs(filters.endDate).format("YYYY-MM-DD") : undefined,
        xml_type: filters.xmlType || undefined,
        search: filters.searchQuery || undefined,
        search_field: filters.searchField || "all",
        exact_match: filters.exactMatch || false,
        document_type: documentType, // Add document type parameter
        unit_price: unitPrice || undefined, // Add unit price parameter
      };

      console.log("Generating PDF report with params:", params);

      // Call the backend endpoint to generate the PDF report
      const response = await api.get(`/api/documents/generate-pdf-report/`, {
        params,
        responseType: "blob", // Expect a blob response
      });

      return response.data; // Return the blob data
    },
    onSuccess: (data) => {
      console.log("PDF generated successfully, creating download link");

      // Create a blob URL from the response data
      const pdfBlob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;

      // Create a dynamic filename or use a default
      const filename = `report_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
      a.download = filename;

      document.body.appendChild(a); // Append the link to the body
      a.click(); // Programmatically click the link to trigger download
      window.URL.revokeObjectURL(url); // Clean up the blob URL
      a.remove(); // Remove the link element

      showSnackbar(t("pdfGeneratedSuccessfully"), "success");
    },
    onError: (error) => {
      console.error("PDF Generation error:", error);

      // Try to extract error message from response
      let errorMessage = "Failed to generate PDF report";

      if (error.message === "Company not selected") {
        errorMessage = t("selectCompanyFirst");
      } else if (error.response?.data) {
        try {
          // For JSON responses
          if (typeof error.response.data === "object") {
            errorMessage = error.response.data.error || error.response.data.message || errorMessage;
          }
          // For blob responses with error
          else if (error.response.data instanceof Blob) {
            // We can't directly read the Blob here, so we use a generic message
            errorMessage = "Server returned an error. Check your filters and try again.";
          }
        } catch (e) {
          console.error("Error parsing error response:", e);
        }
      }

      showSnackbar(errorMessage, "error");
    },
  });

  // Explicitly define handleGeneratePdfReport function
  const handleGeneratePdfReport = () => {
    console.log("handleGeneratePdfReport called");
    openPdfTypeDialog();
  };

  const handleRecognize = (selectedDocumentIds) => {
    if (selectedDocumentIds.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }

    if (!selectedCompany?.id) {
      showSnackbar(t("selectCompanyFirst"), "error");
      return;
    }

    startRecognition(selectedDocumentIds);
  };

  // Recognition options (company code, Markdown) come from the company's settings (Settings → Recognition (AI))
  const startRecognition = (selectedDocumentIds) => {
    const ids = [...selectedDocumentIds];
    if (ids.length === 0) return;

    const startMsgTemplate = t("processingBatchStart") || "Pradedamas {n} dokumentų atpažinimas...";
    showSnackbar(startMsgTemplate.replace("{n}", String(ids.length)), "info");

    // Start recognition in the global context — survives navigation
    startBatch(ids);
    toggleAllRowsSelected(false);
  };

  const handleCancelRecognition = () => {
    cancelBatch();
    showSnackbar(t("recognitionCancelRequested") || "Recognition cancel requested", "info");
  };

  const handleDeleteSelected = (selectedDocumentIds) => {
    if (selectedDocumentIds.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }
    setDocumentsToDelete([...selectedDocumentIds]);
    setDeleteCompanyId(selectedCompany?.id || null);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = () => {
    if (deleteCompanyId !== selectedCompany?.id) {
      showSnackbar(t("companyChanged") || "Company changed. Select documents again.", "error");
      setDeleteConfirmOpen(false);
      setDocumentsToDelete([]);
      setDeleteCompanyId(null);
      return;
    }
    deleteMutation.mutate(documentsToDelete);
  };

  const handleExportXml = (exportType = "agnum", exportWithMatches = false) => {
    // Instead of directly exporting, open the settings dialog
    setXmlExportType(exportType);
    setXmlExportWithMatches(exportWithMatches);
    openXmlExportDialog();
  };

  const handleExportExcel = () => {
    if (selectedFlatRows.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }
    setExportVariant(null);
    setFileExport({ type: "excel", documentIds: selectedFlatRows.map((row) => row.original.id) });
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!selectedCompany) {
      showSnackbar(t("selectCompanyFirst"), "error");
      return;
    }
    if (!files || files.length === 0) return;

    // Upload in batches to avoid Django DATA_UPLOAD_MAX_NUMBER_FILES.
    const INITIAL_BATCH_SIZE = 20;

    // Abort previous upload if still in-flight

    try {
      uploadAbortRef.current?.abort?.();
    } catch (e) {
      // ignore
    }

    const controller = new AbortController();
    uploadAbortRef.current = controller;

    setUploadUi({
      status: "uploading",
      progress: 0,
      totalFiles: files.length,
      files: files.map((f) => f?.name).filter(Boolean),
      message: "",
      failedFiles: [],
    });

    showSnackbar(
      (t("uploadingFiles") || "Uploading {count} files...").replace("{count}", files.length),
      "info"
    );

    const startTime = Date.now();

    // Keep the screen awake on mobile during upload
    let wakeLock = null;

    try {
      if (navigator.wakeLock) {
        wakeLock = await navigator.wakeLock.request("screen");
      }
    } catch (e) {
      // Wake lock not supported or permission denied, proceed anyway
    }

    try {
      const pendingBatches = chunkArray(files, INITIAL_BATCH_SIZE);
      const total = files.length;
      let completed = 0;
      const failedNames = [];
      let pendingSplitUploads = 0;
      let hadErrors = false;

      while (pendingBatches.length > 0) {
        const batchFiles = pendingBatches.shift();
        if (!batchFiles || batchFiles.length === 0) continue;
        if (controller.signal?.aborted) {
          throw Object.assign(new Error("Upload cancelled"), { code: "ERR_CANCELED" });
        }

        setUploadUi((prev) => ({
          ...prev,
          status: "uploading",
          progress: Math.round((completed / total) * 100),
          message: `${t("uploading") || "Uploading"} ${Math.min(
            completed + batchFiles.length,
            total
          )}/${total}`,
          failedFiles: failedNames,
        }));

        const batchForm = new FormData();
        batchForm.append("company_id", selectedCompany.id);
        batchFiles.forEach((file) => batchForm.append("files", file));

        let response;

        try {
          response = await api.post(`/api/upload/`, batchForm, {
            // IMPORTANT: do not set Content-Type manually for FormData.
            timeout: 300000,
            signal: controller.signal,
          });
        } catch (batchErr) {
          const isAborted = batchErr?.name === "CanceledError" || batchErr?.code === "ERR_CANCELED";
          if (isAborted) throw batchErr;

          const status = batchErr?.response?.status;
          const details = batchErr?.response?.data;
          const raw =
            details?.message || details?.error || (typeof details === "string" ? details : null);

          // If server rejects with 400, it can be "too many files" (Bad Request HTML).
          // Try splitting the batch and retrying smaller pieces.
          if (status === 400 && batchFiles.length > 1) {
            const mid = Math.ceil(batchFiles.length / 2);
            const a = batchFiles.slice(0, mid);
            const b = batchFiles.slice(mid);
            // Retry smaller chunks first
            if (b.length) pendingBatches.unshift(b);
            if (a.length) pendingBatches.unshift(a);
            continue;
          }

          hadErrors = true;
          const fallback =
            status === 400
              ? t("badRequest") || "Bad request (400)."
              : t("errorUploadingFiles") || `Error uploading files: ${batchErr.message}`;
          const message = normalizeServerMessage(raw, fallback);
          showSnackbar(message, "error");

          failedNames.push(...batchFiles.map((f) => f?.name).filter(Boolean));
          completed += batchFiles.length;
          continue;
        }

        const failedFiles =
          (Array.isArray(response.data?.failed_files) && response.data.failed_files) ||
          (Array.isArray(response.data?.failedFiles) && response.data.failedFiles) ||
          (Array.isArray(response.data?.errors) && response.data.errors) ||
          [];
        pendingSplitUploads += Number(response.data?.pending_split_uploads || 0);

        if (!response.data?.success) {
          hadErrors = true;
          const msg = normalizeServerMessage(
            response.data?.message,
            t("failedToUploadDocuments") || "Failed to upload documents"
          );
          showSnackbar(msg, "error");
          if (failedFiles.length > 0) {
            failedNames.push(...failedFiles);
          } else {
            failedNames.push(...batchFiles.map((f) => f?.name).filter(Boolean));
          }
        } else if (failedFiles.length > 0) {
          hadErrors = true;
          failedNames.push(...failedFiles);
        }

        completed += batchFiles.length;
        setUploadUi((prev) => ({
          ...prev,
          status: "uploading",
          progress: Math.round((completed / total) * 100),
          failedFiles: failedNames,
        }));
      }

      const uniqueFailed = Array.from(new Set(failedNames.filter(Boolean)));
      const baseFinalMessage =
        uniqueFailed.length > 0
          ? (
              t("uploadCompletedWithIssues") || "Įkėlimas baigtas su problemomis. Nepavyko: {count}"
            ).replace("{count}", uniqueFailed.length)
          : pendingSplitUploads > 0
          ? (
              t("uploadCompletedWithPendingSplit") || "Įkėlimas baigtas. PDF skaidymui: {count}"
            ).replace("{count}", pendingSplitUploads)
          : t("filesUploadedSuccessfully") || "Files uploaded successfully";

      const elapsedSec = Math.round((Date.now() - startTime) / 1000);
      const min = Math.floor(elapsedSec / 60);
      const sec = elapsedSec % 60;
      const timeStr = min > 0 ? `${min} min ${sec} s` : `${sec} s`;
      const finalMessage = `${baseFinalMessage} (Užtruko: ${timeStr})`;

      showSnackbar(finalMessage, uniqueFailed.length > 0 ? "warning" : "success");
      setUploadUi((prev) => ({
        ...prev,
        status: uniqueFailed.length > 0 || hadErrors ? "error" : "success",
        progress: 100,
        message: finalMessage,
        failedFiles: uniqueFailed,
      }));
    } catch (error) {
      const elapsedSec = Math.round((Date.now() - startTime) / 1000);
      const min = Math.floor(elapsedSec / 60);
      const sec = elapsedSec % 60;
      const timeStr = min > 0 ? `${min} min ${sec} s` : `${sec} s`;

      const isAborted = error?.name === "CanceledError" || error?.code === "ERR_CANCELED";
      if (isAborted) {
        const message = `${t("uploadCancelled") || "Upload cancelled"} (Užtruko: ${timeStr})`;
        showSnackbar(message, "warning");
        setUploadUi((prev) => ({
          ...prev,
          status: "error",
          message,
        }));
      } else {
        const details = error.response?.data;
        const status = error.response?.status;
        const raw =
          details?.message || details?.error || (typeof details === "string" ? details : null);
        const fallback =
          status === 400
            ? t("badRequest") || "Bad request (400)."
            : t("errorUploadingFiles") || `Error uploading files: ${error.message}`;
        const baseMessage = normalizeServerMessage(raw, fallback);
        const message = `${baseMessage} (Užtruko: ${timeStr})`;
        showSnackbar(message, "error");
        console.error("Upload error:", error);
        setUploadUi((prev) => ({
          ...prev,
          status: "error",
          progress: 100,
          message,
        }));
      }
    } finally {
      // Release wake lock so screen can turn off again

      try {
        if (wakeLock) await wakeLock.release();
      } catch (e) {
        // ignore
      }
      queryClient.invalidateQueries(["documents", selectedCompany?.id, filters]);
      queryClient.invalidateQueries(["pdf-split-jobs", selectedCompany?.id, "pending"]);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));
      event.target.value = null;
      uploadAbortRef.current = null;
    }
  };

  const handleRecognizeUnrecognized = () => {
    const unrecognizedDocuments = (documents || []).filter((doc) => !doc.is_recognized);
    const selectedDocumentIds = unrecognizedDocuments.map((doc) => doc.id);
    if (selectedDocumentIds.length === 0) {
      showSnackbar("No unrecognized documents found for the selected company.", "info");
      return;
    }
    startRecognition(selectedDocumentIds);
  };

  // Details dialog handlers
  const openDetailsDialog = (doc) => {
    setSelectedDocument(doc);
    setDetailsDialogOpen(true);
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedDocument(null);
  };

  const persistDocumentNavigation = () => {
    if (!selectedCompany?.id || !Array.isArray(documents)) return;
    window.sessionStorage.setItem(
      `documentNavigation:${selectedCompany.id}`,
      JSON.stringify({ documentIds: documents.map((document) => document.id) })
    );
  };

  const columns = useMemo(() => {
    const baseColumns = documentTableData(t, openDetailsDialog, persistDocumentNavigation).columns;
    if (documentTab !== "sale") return baseColumns;
    return baseColumns.map((column) => {
      const swap = SALE_PARTY_COLUMNS[column.accessor];
      return swap
        ? { ...column, id: column.accessor, accessor: swap.accessor, Header: t(swap.header) }
        : column;
    });
  }, [t, documents, selectedCompany?.id, documentTab]);

  // Always provide an array to react-table — merge live overlays from context for immediate row updates
  const data = useMemo(() => {
    const base = Array.isArray(documents) ? documents : [];
    if (!docOverlays || docOverlays.size === 0) return base;
    return base.map((doc) => docOverlays.get(doc.id) || doc);
  }, [documents, docOverlays]);

  // Options for "per page" selector, responsive to screen size
  const perPageOptions = useMemo(
    () => (isMdDown ? [20, 50, 100] : [50, 100, 200, 500]),
    [isMdDown]
  );

  const defaultHiddenColumns = useMemo(
    () => [
      "due_date",
      "net_amount",
      "total_tax_amount",
      "receiver_name",
      "receiver_registration",
      "supplier_address",
      "supplier_iban",
      "notes",
    ],
    []
  );

  // Header checkbox to select all rows on the current page
  const SelectionHeader = ({ getToggleAllPageRowsSelectedProps }) => {
    return <Checkbox {...getToggleAllPageRowsSelectedProps()} color="primary" />;
  };

  SelectionHeader.propTypes = {
    getToggleAllPageRowsSelectedProps: PropTypes.func.isRequired,
  };

  const SelectionCell = ({ row }) => {
    return <Checkbox {...row.getToggleRowSelectedProps()} color="primary" />;
  };

  SelectionCell.propTypes = {
    row: PropTypes.shape({
      getToggleRowSelectedProps: PropTypes.func.isRequired,
    }).isRequired,
  };

  // Processing spinner cell with PropTypes to satisfy eslint
  const ProcessingCell = ({ row }) => {
    return processingIds.has(row.original.id) ? (
      <MDBox display="flex" alignItems="center" justifyContent="center" px={0.5}>
        <CircularProgress size={18} thickness={5} color="primary" />
      </MDBox>
    ) : null;
  };

  ProcessingCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired,
    }).isRequired,
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    toggleAllRowsSelected,
    allColumns,
    setHiddenColumns,
    state: {
      pageSize,
      pageIndex: tablePageIndex,
      selectedRowIds,
      hiddenColumns,
      sortBy: tableSortBy,
    },
  } = useTable(
    {
      columns,
      data,
      getRowId: (row) => String(row.id),
      initialState: {
        pageSize: pageSizeValue,
        pageIndex: pageIndex,
        hiddenColumns: (() => {
          try {
            const saved = localStorage.getItem(`documentPage_hiddenCols_v3_${selectedCompany?.id}`);
            return saved ? JSON.parse(saved) : defaultHiddenColumns;
          } catch (e) {
            return defaultHiddenColumns;
          }
        })(),
      },
      manualPagination: true, // Backend handles pagination
      manualSortBy: true,
      autoResetSortBy: false,
      pageCount: totalPages, // Tell react-table how many pages exist
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Per-row processing indicator
        {
          id: "processing",
          Header: () => null,
          Cell: ProcessingCell,
          width: 20,
        },
        {
          id: "selection",
          Header: SelectionHeader,
          Cell: SelectionCell,
          width: 34,
        },
        ...columns,
      ]);
    }
  );

  useEffect(() => {
    setSortBy(tableSortBy);
    setPageIndex(0);
  }, [tableSortBy]);

  const initializedCompanyRef = useRef(null);

  // Load and save hidden columns without wiping out on switch
  useEffect(() => {
    if (selectedCompany?.id) {
      if (initializedCompanyRef.current !== selectedCompany.id) {
        // First render for this company: Load from local storage
        initializedCompanyRef.current = selectedCompany.id;

        try {
          const saved = localStorage.getItem(`documentPage_hiddenCols_v3_${selectedCompany.id}`);
          if (saved) {
            setHiddenColumns(JSON.parse(saved));
          } else {
            setHiddenColumns(defaultHiddenColumns);
          }
        } catch (e) {
          console.error("Failed to load hidden columns", e);
        }
      } else {
        // Company is unchanged (state updated through UI): save to localStorage
        localStorage.setItem(
          `documentPage_hiddenCols_v3_${selectedCompany.id}`,
          JSON.stringify(hiddenColumns || [])
        );
      }
    }
  }, [defaultHiddenColumns, hiddenColumns, selectedCompany?.id, setHiddenColumns]);

  // Keep react-table's internal pageIndex reset to 0 whenever data changes (backend returns new page)
  useEffect(() => {
    gotoPage(0);
  }, [data, gotoPage]);

  // Keep react-table's internal pageIndex in sync with our external pageIndex
  useEffect(() => {
    if (tablePageIndex !== pageIndex) {
      gotoPage(pageIndex);
    }
  }, [pageIndex, tablePageIndex, gotoPage]);

  const handleResetFilters = () => {
    setFilters({
      suppliers: [],
      excludeSuppliers: false,
      documentTypes: [],
      excludeDocumentTypes: false,
      dateType: "invoice",
      startDate: null,
      endDate: null,
      xmlType: "",
      searchQuery: "",
      searchField: "all",
      exactMatch: false,
      recognitionStatus: "",
      advanceReport: "",
    });
  };

  useEffect(() => {
    if (selectedCompany) {
      handleResetFilters();
      setDocumentTab("unrecognized");
      setTabCounts({});
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (previousIsMdDownRef.current === isMdDown) return;
    previousIsMdDownRef.current = isMdDown;
    const allowedSizes = isMdDown ? [20, 50, 100] : [50, 100, 200, 500];
    if (!allowedSizes.includes(pageSizeValue)) {
      const target = isMdDown ? 50 : 100;
      setPageSizeValue(target);
      setPageSize(target);
      setPageIndex(0);
    }
  }, [isMdDown, pageSizeValue, setPageSize]);

  // Guard: if totalPages shrinks (e.g., after recognition/filter change), keep pageIndex in range
  useEffect(() => {
    if (typeof totalPages === "number" && totalPages > 0 && pageIndex >= totalPages) {
      setPageIndex(Math.max(0, totalPages - 1));
    }
  }, [totalPages, pageIndex]);

  // If current page becomes empty while there are items overall, jump to last valid page
  useEffect(() => {
    if (!documents) return;
    const overall = documents.length;
    const currentCount = rows?.length ?? 0;
    if (overall > 0 && currentCount === 0 && totalPages > 0) {
      setPageIndex(Math.max(0, Math.min(pageIndex, totalPages - 1)));
    }
  }, [documents, rows?.length, totalPages]);

  const selectedDocumentIds = useMemo(
    () => selectedFlatRows.map((row) => row.original.id),
    [selectedFlatRows]
  );

  useEffect(() => {
    toggleAllRowsSelected(false);
  }, [
    filters,
    documentTab,
    pageIndex,
    pageSizeValue,
    sortBy,
    selectedCompany?.id,
    toggleAllRowsSelected,
  ]);

  const handlePageChangeInternal = (event, value) => {
    setPageIndex(value - 1);
  };

  const handleDocumentsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSizeValue(newSize);
    setPageIndex(0); // Reset to first page when changing page size
    console.log(`Changed page size to ${newSize}`);
    // Keep react-table page size in sync with selection
    setPageSize(newSize);
  };

  // XML Export dialog handlers
  const openXmlExportDialog = () => {
    if (selectedFlatRows.length === 0) {
      showSnackbar(t("noDocumentsSelected"), "error");
      return;
    }
    setExportVariant(null);
    setXmlExportDialogOpen(true);
  };

  const handleXmlExportDialogClose = () => {
    if (exportSaving) return;
    setExportVariant(null);
    setXmlExportDialogOpen(false);
    setSelectedPozymiai([]);
    setIncludePvmInTotal(true);
    setXmlExportType("agnum");
    setXmlExportWithMatches(false);
    setSpacyModelSize("lg");
    setMatcherType("llm");
    setOverridePvmCode("");
    setRecalculateVat(false);
    setIsBusinessTrip(false);
    setBusinessTripDocumentNumber("");
  };

  const handlePozymiaiToggle = (pozymiusId) => {
    setSelectedPozymiai((prev) =>
      prev.includes(pozymiusId) ? prev.filter((id) => id !== pozymiusId) : [...prev, pozymiusId]
    );
  };

  const beginXmlExport = (exportParams) => {
    sessionStorage.removeItem("pendingSupplierExport");
    setPendingSupplierExport(null);
    const exportingMsg = (t("exportingXml") || "Exporting {count} documents...").replace(
      "{count}",
      exportParams.document_ids.length
    );
    showSnackbar(exportingMsg, "info");
    startExport(exportParams);
  };

  const continueXmlExport = async (exportParams) => {
    if (!exportParams.use_matches || exportParams.exportType === "agnum_summary") {
      beginXmlExport(exportParams);
      return;
    }
    setMatchReview({
      open: true,
      loading: true,
      items: [],
      companyId: null,
      defaultWarehouseCode: "",
      defaultGroup: "Prekės",
      exportParams,
    });
    showSnackbar("Tikrinami sandėlio atitikmenys prieš XML formavimą…", "info");
    try {
      const response = await api.post("/api/match-review/", {
        document_ids: exportParams.document_ids,
        matcher_type: exportParams.matcher_type,
        spacy_model_size: exportParams.spacy_model_size,
      });
      const review = response.data;
      if ((review.unmatched || []).length) {
        setMatchReview({
          open: true,
          loading: false,
          items: review.unmatched,
          companyId: review.company_id,
          defaultWarehouseCode: review.default_warehouse_code,
          defaultGroup: review.default_group,
          exportParams,
        });
      } else {
        setMatchReview((previous) => ({ ...previous, open: false, loading: false }));
        beginXmlExport(exportParams);
      }
    } catch (requestError) {
      setMatchReview((previous) => ({ ...previous, open: false, loading: false }));
      showSnackbar(
        requestError.response?.data?.error || "Nepavyko patikrinti sandėlio atitikmenų.",
        "error"
      );
    }
  };

  const checkSupplierPreflight = async (exportParams) => {
    setSupplierPreflight({ open: true, loading: true, items: [], exportParams });
    try {
      const response = await api.post("/api/export-preflight/", {
        document_ids: exportParams.document_ids,
      });
      const items = response.data.missing_supplier_infos || [];
      if (items.length) {
        setSupplierPreflight({ open: true, loading: false, items, exportParams });
      } else {
        setSupplierPreflight({ open: false, loading: false, items: [], exportParams: null });
        continueXmlExport(exportParams);
      }
    } catch (requestError) {
      setSupplierPreflight({ open: false, loading: false, items: [], exportParams: null });
      showSnackbar(requestError.response?.data?.error || t("supplierPreflightFailed"), "error");
    }
  };

  const handleXmlExportConfirm = async () => {
    if (exportSaving || !isExportVariantValid(exportVariant)) return;
    const documentIds = selectedFlatRows.map((row) => row.original.id);
    const typeLabel =
      xmlExportType === "agnum"
        ? t("purchases") || "Purchases"
        : xmlExportType === "sales"
        ? t("salesInvoices") || "Sales"
        : t("summary") || "Summary";
    const exportParams = {
      exportType: xmlExportType,
      document_ids: documentIds,
      use_matches: xmlExportWithMatches,
      matcher_type: matcherType,
      pozymiai: selectedPozymiai,
      include_pvm_in_total: includePvmInTotal,
      spacy_model_size: spacyModelSize,
      override_pvm_code: overridePvmCode,
      recalculate_vat: recalculateVat,
      is_business_trip: isBusinessTrip,
      business_trip_document_number: businessTripDocumentNumber,
      exportLabel: `${typeLabel} · ${documentIds.length} ${t("documents") || "docs"}`,
    };
    setExportSaving(true);
    try {
      await assignExportVariant(documentIds);
      setXmlExportDialogOpen(false);
      checkSupplierPreflight(exportParams);
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Nepavyko priskirti varianto.", "error");
    } finally {
      setExportSaving(false);
    }
  };

  const handleFileExportConfirm = async () => {
    if (exportSaving || !fileExport || !isExportVariantValid(exportVariant)) return;
    setExportSaving(true);
    try {
      await assignExportVariant(fileExport.documentIds);
      if (fileExport.type === "excel") {
        exportExcelMutation.mutate({
          document_ids: fileExport.documentIds,
          use_matches: false,
          spacy_model_size: "lg",
        });
      } else {
        exportRodaMutation.mutate({ document_ids: fileExport.documentIds, include_unit: true });
      }
      setFileExport(null);
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Nepavyko priskirti varianto.", "error");
    } finally {
      setExportSaving(false);
    }
  };

  // Keep react-table page size synced when pageSizeValue changes (e.g., selecting 500)
  useEffect(() => {
    setPageSize(pageSizeValue);
  }, [pageSizeValue, setPageSize]);

  // Reset overlays when the dataset context changes
  useEffect(() => {
    clearOverlays();
  }, [selectedCompany?.id, filters, documentTab, pageIndex, pageSizeValue, clearOverlays]);

  // Hydrate filters and pagination from sessionStorage when opening the page (per company)
  useEffect(() => {
    if (!selectedCompany?.id) return;
    if (hydratedCompanyIdRef.current === selectedCompany.id) return;

    try {
      const raw = sessionStorage.getItem(`documentsPage:${selectedCompany.id}`);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          if (saved.filters) {
            setFilters({
              ...saved.filters,
              documentTypes: [],
              excludeDocumentTypes: false,
              recognitionStatus: "",
            });
          }
          if (
            ["unrecognized", "purchase", "sale", "return", "other", "unknown"].includes(
              saved.documentTab
            )
          ) {
            setDocumentTab(saved.documentTab);
          }
          if (typeof saved.pageIndex === "number") setPageIndex(saved.pageIndex);
          if (typeof saved.pageSizeValue === "number") {
            const allowedSizes = isMdDown ? [20, 50, 100] : [50, 100, 200, 500];
            const savedSize = allowedSizes.includes(saved.pageSizeValue)
              ? saved.pageSizeValue
              : isMdDown
              ? 50
              : 100;
            setPageSizeValue(savedSize);
            setPageSize(savedSize);
          }
          if (typeof saved.scrollY === "number") savedScrollPositionRef.current = saved.scrollY;
        }
      }
    } catch (e) {
      console.error("Failed to hydrate documents page state:", e);
    } finally {
      hydratedCompanyIdRef.current = selectedCompany.id;
      setHydratedCompanyId(selectedCompany.id);
    }
  }, [selectedCompany?.id, isMdDown, setPageSize]);

  useEffect(() => {
    if (documentsLoading || savedScrollPositionRef.current === null) return;
    const scrollY = savedScrollPositionRef.current;
    const restoreScroll = () => {
      window.scrollTo({ top: scrollY, behavior: "auto" });
      savedScrollPositionRef.current = null;
    };
    const animationFrame = window.requestAnimationFrame(restoreScroll);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [documentsLoading, documents?.length]);

  useEffect(() => {
    if (!selectedCompany?.id) {
      setSavedViews([]);
      return;
    }

    try {
      const storedViews = JSON.parse(
        localStorage.getItem(`documentSavedViews:${selectedCompany.id}`) || "[]"
      );
      setSavedViews(Array.isArray(storedViews) ? storedViews : []);
    } catch (error) {
      setSavedViews([]);
    }
  }, [selectedCompany?.id]);

  const saveCurrentView = () => {
    const name = savedViewName.trim();
    if (!name || !selectedCompany?.id) return;
    const nextViews = [
      ...savedViews.filter((view) => view.name !== name),
      { name, filters, documentTab, pageSizeValue },
    ];
    localStorage.setItem(`documentSavedViews:${selectedCompany.id}`, JSON.stringify(nextViews));
    setSavedViews(nextViews);
    setSavedViewName("");
    setSaveViewDialogOpen(false);
    showSnackbar(t("viewSaved") || "View saved", "success");
  };

  const applySavedView = (name) => {
    const view = savedViews.find((candidate) => candidate.name === name);
    if (!view) return;
    setFilters({
      ...view.filters,
      documentTypes: [],
      excludeDocumentTypes: false,
      recognitionStatus: "",
    });
    if (
      ["unrecognized", "purchase", "sale", "return", "other", "unknown"].includes(view.documentTab)
    ) {
      setDocumentTab(view.documentTab);
    }
    const allowedSizes = isMdDown ? [20, 50, 100] : [50, 100, 200, 500];
    setPageSizeValue(
      allowedSizes.includes(view.pageSizeValue) ? view.pageSizeValue : isMdDown ? 50 : 100
    );
    setPageIndex(0);
  };

  // Persist filters and pagination to sessionStorage on change (per company)
  useEffect(() => {
    if (!selectedCompany?.id || hydratedCompanyId !== selectedCompany.id) return;
    const toSave = {
      filters,
      documentTab,
      pageIndex,
      pageSizeValue,
      scrollY: savedScrollPositionRef.current || window.scrollY,
    };

    try {
      sessionStorage.setItem(`documentsPage:${selectedCompany.id}`, JSON.stringify(toSave));
    } catch (e) {
      // Non-fatal
      console.warn("Failed to save documents page state:", e);
    }
  }, [selectedCompany?.id, hydratedCompanyId, filters, documentTab, pageIndex, pageSizeValue]);

  useEffect(() => {
    if (!selectedCompany?.id) return undefined;
    const persistScrollPosition = () => {
      try {
        const key = `documentsPage:${selectedCompany.id}`;
        const saved = JSON.parse(sessionStorage.getItem(key) || "{}");
        sessionStorage.setItem(key, JSON.stringify({ ...saved, scrollY: window.scrollY }));
      } catch (error) {
        // Scroll restoration is non-critical.
      }
    };
    window.addEventListener("pagehide", persistScrollPosition);
    return () => {
      persistScrollPosition();
      window.removeEventListener("pagehide", persistScrollPosition);
    };
  }, [selectedCompany?.id]);

  const visibleTableColumns = headerGroups[headerGroups.length - 1]?.headers || [];
  const minimumTableWidth = visibleTableColumns.reduce(
    (width, column) =>
      width +
      (expandableTableColumns.has(column.id)
        ? column.id === "title"
          ? 180
          : 150
        : fixedTableColumnWidths[column.id] || 120),
    0
  );

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <MDBox
                px={{ xs: 2, md: 3 }}
                py={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
              >
                <MDTypography variant="h5" fontWeight="medium">
                  {t("documentsManagement")}
                </MDTypography>
                {typeof totalCount !== "undefined" && totalCount > 0 && (
                  <MDTypography variant="body2" color="text" fontWeight="medium">
                    {t("totalDocuments") || "Rasta dokumentų:"} {totalCount}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox p={{ xs: 2, md: 2.5 }} sx={{ overflowX: "auto" }}>
                {pendingSupplierExport &&
                  String(pendingSupplierExport.companyId) === String(selectedCompany?.id) && (
                    <Alert
                      severity="info"
                      sx={{ mb: 2, alignItems: "center" }}
                      action={
                        <MDBox display="flex" gap={1} flexWrap="wrap">
                          <MDButton
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={() =>
                              checkSupplierPreflight(pendingSupplierExport.exportParams)
                            }
                          >
                            {t("resumeSupplierExport")}
                          </MDButton>
                          <MDButton
                            variant="outlined"
                            color="secondary"
                            size="small"
                            onClick={() => {
                              sessionStorage.removeItem("pendingSupplierExport");
                              setPendingSupplierExport(null);
                            }}
                          >
                            {t("cancel")}
                          </MDButton>
                        </MDBox>
                      }
                    >
                      {t("pendingSupplierExportMessage").replace(
                        "{count}",
                        pendingSupplierExport.exportParams.document_ids.length
                      )}
                    </Alert>
                  )}
                {/* Recognition progress/cancel is now shown in the global floating panel (RecognitionProgressPanel) */}
                {pendingPdfSplitJobs.length > 0 && (
                  <Alert
                    severity="warning"
                    icon={<CallSplitIcon />}
                    sx={{ mb: 2, alignItems: "center" }}
                    action={
                      <MDButton
                        color="warning"
                        variant="contained"
                        size="small"
                        onClick={() => navigate("/pdf-split")}
                      >
                        {t("openPdfSplitQueue")}
                      </MDButton>
                    }
                  >
                    <MDBox display="flex" flexDirection="column" gap={0.75}>
                      <MDTypography variant="button" fontWeight="medium">
                        {(t("pendingPdfSplitsNotice") || "PDF skaidymui laukia: {count}").replace(
                          "{count}",
                          pendingPdfSplitJobs.length
                        )}
                      </MDTypography>
                      <MDBox display="flex" gap={0.75} flexWrap="wrap">
                        {pendingPdfSplitJobs.slice(0, 4).map((job) => (
                          <Chip
                            key={job.id}
                            size="small"
                            variant="outlined"
                            label={`${job.original_filename} · ${job.page_count} ${
                              t("pagesShort") || "psl."
                            }`}
                          />
                        ))}
                        {pendingPdfSplitJobs.length > 4 && (
                          <Chip
                            size="small"
                            color="warning"
                            label={`+${pendingPdfSplitJobs.length - 4}`}
                          />
                        )}
                      </MDBox>
                    </MDBox>
                  </Alert>
                )}
                {pendingPdfSplitsLoading && pendingPdfSplitJobs.length === 0 && null}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        mb: 1.5,
                        p: { xs: 1.25, md: 1.5 },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                      }}
                    >
                      <MDBox sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }}>
                        <Tabs
                          value={documentTab}
                          onChange={(event, nextTab) => {
                            setDocumentTab(nextTab);
                            setPageIndex(0);
                            toggleAllRowsSelected(false);
                          }}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          aria-label={t("documentGroups")}
                        >
                          {[
                            { id: "unrecognized", label: t("unrecognized") },
                            { id: "purchase", label: t("purchaseDocument") },
                            { id: "sale", label: t("salesDocument") },
                            { id: "return", label: t("returnDocument") },
                            { id: "other", label: t("otherDocument") },
                            ...(tabCounts.unknown > 0 || documentTab === "unknown"
                              ? [{ id: "unknown", label: t("documentTypeUnknown") }]
                              : []),
                          ].map(({ id, label }) => (
                            <Tab
                              key={id}
                              value={id}
                              disabled={!selectedCompany}
                              label={
                                <MDBox display="flex" alignItems="center" gap={0.75}>
                                  <span>{label}</span>
                                  <Chip label={tabCounts[id] ?? 0} size="small" />
                                </MDBox>
                              }
                            />
                          ))}
                        </Tabs>
                      </MDBox>
                      <FilterBar
                        filters={filters}
                        hideClassificationFilters
                        setFilters={(nextFilters) => {
                          setPageIndex(0);
                          setFilters({
                            ...nextFilters,
                            documentTypes: [],
                            excludeDocumentTypes: false,
                            recognitionStatus: "",
                          });
                        }}
                        documentCount={totalCount}
                      >
                        {({ filterButton, activeChips }) => (
                          <SearchBar
                            onSearch={handleSearch}
                            initialQuery={filters.searchQuery}
                            initialField={filters.searchField}
                            initialExact={filters.exactMatch}
                            filterButton={filterButton}
                            filterChips={activeChips}
                            trailingActions={
                              <>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: { xs: "100%", sm: 180 } }}
                                >
                                  <InputLabel>{t("exportStatus")}</InputLabel>
                                  <Select
                                    value={filters.exportStatus || ""}
                                    label={t("exportStatus")}
                                    onChange={(event) => {
                                      setFilters((current) => ({
                                        ...current,
                                        exportStatus: event.target.value,
                                      }));
                                      setPageIndex(0);
                                      toggleAllRowsSelected(false);
                                    }}
                                  >
                                    <MenuItem value="">{t("allDocuments")}</MenuItem>
                                    <MenuItem value="not_exported">{t("notExported")}</MenuItem>
                                    <MenuItem value="exported">{t("exported")}</MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: { xs: "100%", sm: 220 } }}
                                >
                                  <InputLabel>{t("savedViews") || "Saved views"}</InputLabel>
                                  <Select
                                    value=""
                                    label={t("savedViews") || "Saved views"}
                                    onChange={(event) => applySavedView(event.target.value)}
                                    disabled={savedViews.length === 0}
                                  >
                                    {savedViews.map((view) => (
                                      <MenuItem key={view.name} value={view.name}>
                                        {view.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <MDButton
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  onClick={() => setSaveViewDialogOpen(true)}
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  {t("saveView") || "Save view"}
                                </MDButton>
                              </>
                            }
                          />
                        )}
                      </FilterBar>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1.5}
                      sx={{
                        backgroundColor: "background.paper",
                        p: 1.5,
                        borderRadius: 1.5,
                        boxShadow: 1,
                        overflowX: "auto",
                      }}
                    >
                      <ActionButtons
                        onRecognizeUnrecognized={() => handleRecognizeUnrecognized()}
                        onExportXml={handleExportXml}
                        onExportExcel={handleExportExcel}
                        onExportRoda={handleExportRoda}
                        onUpload={handleUpload}
                        uploading={uploadUi.status === "uploading"}
                        uploadProgress={
                          typeof uploadUi.progress === "number" ? uploadUi.progress : undefined
                        }
                        onGeneratePdfReport={handleGeneratePdfReport}
                        isLoading={generatePdfMutation.isPending}
                        unrecognizedCount={
                          (documents || []).filter((document) => !document.is_recognized).length
                        }
                        selectedCount={selectedDocumentIds.length}
                      />
                      <ColumnManager allColumns={allColumns} />
                    </MDBox>
                  </Grid>
                </Grid>
                {documentsLoading ? (
                  <Loader />
                ) : documentsError ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {documentsError.message ||
                      t("errorLoadingDocuments") ||
                      "Error loading documents"}
                  </Alert>
                ) : !selectedCompany ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t("selectCompanyFirst")}
                  </Alert>
                ) : documents && documents.length > 0 ? (
                  <>
                    {isMdDown ? (
                      // Mobile: card list view
                      <MDBox display="flex" flexDirection="column" gap={2}>
                        {rows.map((row, rowIndex) => {
                          prepareRow(row);
                          const o = row.original || {};
                          const isSelected = row.isSelected;
                          const totalsMismatch =
                            !!o.totalsMismatch ||
                            (checkTotalsMismatch ? checkTotalsMismatch(o.data || o) : false);
                          const lineItemsMismatch =
                            !!o.lineItemsMismatch ||
                            (checkLineItemsMismatch
                              ? checkLineItemsMismatch({
                                  ...(o.data || o),
                                  line_items: o.line_items,
                                })
                              : false);
                          const isValid =
                            o.isValid !== false && !totalsMismatch && !lineItemsMismatch;
                          const supplier =
                            o.supplier_name ||
                            o.supplier ||
                            o.supplier_title ||
                            o.supplierName ||
                            "";
                          const invoiceNo =
                            o.invoice_id || o.invoice_number || o.number || o.invoice || "";
                          const dateVal = o.invoice_date || o.date || o.document_date || null;
                          const total =
                            o.total || o.total_amount || o.total_sum || o.amount_total || null;
                          const docTypeMeta = getDocumentTypeMeta(o, t);

                          return (
                            <Card
                              key={o.id || rowIndex}
                              style={{
                                border: !isValid
                                  ? "2px solid #e65100"
                                  : isSelected
                                  ? "2px solid rgba(25, 118, 210, 0.5)"
                                  : `1px solid ${theme.palette.divider}`,
                                borderLeft: !isValid ? "6px solid #e65100" : undefined,
                                backgroundColor: isValid
                                  ? theme.palette.background.paper
                                  : isSelected
                                  ? "rgba(255, 152, 0, 0.28)"
                                  : "rgba(255, 152, 0, 0.14)",
                                borderRadius: 8,
                              }}
                            >
                              <MDBox p={2} display="flex" flexDirection="column" gap={1}>
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                >
                                  <MDTypography variant="button" fontWeight="medium">
                                    {supplier || t("unknownSupplier") || ""}
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    {o.last_exported_at && (
                                      <Tooltip
                                        title={`${t("lastExported")}: ${dayjs(
                                          o.last_exported_at
                                        ).format("YYYY-MM-DD HH:mm")}`}
                                      >
                                        <Chip
                                          label={t("exported")}
                                          color="success"
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    )}
                                    {docTypeMeta && (
                                      <Chip
                                        label={docTypeMeta.label}
                                        color={docTypeMeta.chipColor}
                                        variant={docTypeMeta.chipVariant}
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    )}
                                    {processingIds.has(o.id) && (
                                      <MDBox display="flex" alignItems="center" gap={0.5}>
                                        <CircularProgress size={18} thickness={5} />
                                        <MDTypography variant="caption" color="info">
                                          {t("processing") || "Processing..."}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                    {(() => {
                                      const toggleProps = row.getToggleRowSelectedProps();
                                      return (
                                        <Checkbox
                                          {...toggleProps}
                                          color="primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleProps.onClick && toggleProps.onClick(e);
                                          }}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleProps.onChange && toggleProps.onChange(e);
                                          }}
                                        />
                                      );
                                    })()}
                                  </MDBox>
                                </MDBox>
                                <MDTypography variant="caption" color="text">
                                  {t("invoice") || "Invoice"} #: {invoiceNo || "—"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {t("date") || "Date"}:{" "}
                                  {dateVal ? dayjs(dateVal).format("YYYY-MM-DD") : "—"}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {t("total") || "Total"}: {total != null ? total : "—"}{" "}
                                  {total != null ? o.currency || "" : ""}
                                </MDTypography>
                                {!isValid && (
                                  <MDTypography variant="caption" color="warning.main">
                                    {lineItemsMismatch
                                      ? t("lineItemsMismatch") || "Eilučių suma nesutampa"
                                      : totalsMismatch
                                      ? t("validationTotalsMismatch") || "Sumos nesutampa"
                                      : t("documentRequiresReview") ||
                                        "Reikia patikrinti dokumentą"}
                                  </MDTypography>
                                )}
                                {o.is_recognized === false && (
                                  <MDTypography variant="caption" color="warning">
                                    {t("notRecognized") || "Not recognized"}
                                  </MDTypography>
                                )}

                                <MDBox mt={1} display="flex" gap={2} justifyContent="flex-end">
                                  <Tooltip title={t("details") || "Details"}>
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDetailsDialog(o);
                                      }}
                                    >
                                      <InfoIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("view") || "View"}>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        persistDocumentNavigation();
                                        navigate(`/documents/${o.id}`, {
                                          state: { from: { pathname: "/documents" } },
                                        });
                                      }}
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  </Tooltip>
                                </MDBox>
                              </MDBox>
                            </Card>
                          );
                        })}
                      </MDBox>
                    ) : (
                      // Desktop: table view
                      <div
                        style={{
                          overflowX: "auto",
                          maxHeight: "76vh",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            minWidth: `${minimumTableWidth}px`,
                            tableLayout: "fixed",
                            borderCollapse: "collapse",
                          }}
                          aria-label={t("documentsManagement")}
                        >
                          <colgroup>
                            {visibleTableColumns.map((column) => (
                              <col
                                key={column.id}
                                style={
                                  expandableTableColumns.has(column.id)
                                    ? undefined
                                    : { width: `${fixedTableColumnWidths[column.id] || 120}px` }
                                }
                              />
                            ))}
                          </colgroup>
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                              backgroundColor: theme.palette.background.paper,
                            }}
                          >
                            {headerGroups.map((headerGroup, index) => (
                              <tr key={headerGroup.id || index}>
                                {headerGroup.headers.map((column, colIndex) => (
                                  <th
                                    key={column.id || colIndex}
                                    aria-sort={
                                      column.canSort
                                        ? column.isSorted
                                          ? column.isSortedDesc
                                            ? "descending"
                                            : "ascending"
                                          : "none"
                                        : undefined
                                    }
                                    style={{
                                      borderBottom: `1px solid ${theme.palette.divider}`,
                                      padding: "8px 8px",
                                      textAlign: [
                                        "net_amount",
                                        "total_tax_amount",
                                        "total_amount",
                                      ].includes(column.id)
                                        ? "right"
                                        : "left",
                                      cursor: "default",
                                      backgroundColor: theme.palette.background.paper,
                                      color: theme.palette.text.secondary,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {column.canSort ? (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          column.toggleSortBy(!column.isSortedDesc);
                                          setPageIndex(0);
                                        }}
                                        style={{
                                          border: 0,
                                          padding: 0,
                                          background: "none",
                                          color: "inherit",
                                          font: "inherit",
                                          fontWeight: "inherit",
                                          cursor: "pointer",
                                          textAlign: "inherit",
                                        }}
                                      >
                                        {column.render("Header")}
                                        {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}
                                      </button>
                                    ) : (
                                      column.render("Header")
                                    )}
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody>
                            {rows.map((row, rowIndex) => {
                              prepareRow(row);
                              const isSelected = row.isSelected;
                              const isProcessing = processingIds.has(row.original.id);
                              const totalsMismatch =
                                !!row.original.totalsMismatch ||
                                (checkTotalsMismatch
                                  ? checkTotalsMismatch(row.original.data || row.original)
                                  : false);
                              const lineItemsMismatch =
                                !!row.original.lineItemsMismatch ||
                                (checkLineItemsMismatch
                                  ? checkLineItemsMismatch({
                                      ...(row.original.data || row.original),
                                      line_items: row.original.line_items,
                                    })
                                  : false);
                              const isValid =
                                row.original.isValid !== false &&
                                !totalsMismatch &&
                                !lineItemsMismatch;
                              return (
                                <tr
                                  key={row.original.id || rowIndex}
                                  data-doc-id={row.original.id}
                                  style={{
                                    backgroundColor: isProcessing
                                      ? theme.palette.info.light
                                      : !isValid
                                      ? isSelected
                                        ? "rgba(255, 152, 0, 0.28)"
                                        : "rgba(255, 152, 0, 0.14)"
                                      : isSelected
                                      ? theme.palette.action.selected
                                      : theme.palette.background.paper,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected && !isProcessing) {
                                      e.currentTarget.style.backgroundColor = isValid
                                        ? theme.palette.action.hover
                                        : "rgba(255, 152, 0, 0.24)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected && !isProcessing) {
                                      e.currentTarget.style.backgroundColor = isValid
                                        ? theme.palette.background.paper
                                        : "rgba(255, 152, 0, 0.14)";
                                    }
                                  }}
                                >
                                  {row.cells.map((cell, cellIndex) => (
                                    <td
                                      key={cell.column.id || cellIndex}
                                      style={{
                                        backgroundColor: "inherit",
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        borderLeft:
                                          cellIndex === 0
                                            ? isProcessing
                                              ? `5px solid ${theme.palette.info.main}`
                                              : !isValid
                                              ? "5px solid #e65100"
                                              : "5px solid transparent"
                                            : undefined,
                                        padding: "7px 8px",
                                        fontSize: "0.875rem",
                                        lineHeight: 1.35,
                                        textAlign: [
                                          "net_amount",
                                          "total_tax_amount",
                                          "total_amount",
                                        ].includes(cell.column.id)
                                          ? "right"
                                          : "left",
                                      }}
                                    >
                                      {cell.render("Cell")}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls */}
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={3}
                      flexWrap="wrap"
                    >
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <MDTypography variant="body2" color="text">
                          {`${pageIndex * pageSizeValue + 1}–${Math.min(
                            (pageIndex + 1) * pageSizeValue,
                            totalCount
                          )} ${t("of") || "iš"} ${totalCount}`}
                        </MDTypography>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>{t("perPage")}</InputLabel>
                          <Select
                            value={pageSizeValue}
                            onChange={handleDocumentsPerPageChange}
                            label={t("perPage")}
                          >
                            {perPageOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </MDBox>
                      <Pagination
                        count={totalPages}
                        page={pageIndex + 1}
                        onChange={handlePageChangeInternal}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </MDBox>
                  </>
                ) : (
                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                    action={
                      selectedCompany &&
                      (filters.searchQuery ||
                        filters.suppliers.length ||
                        filters.documentTypes.length ||
                        filters.startDate ||
                        filters.endDate ||
                        filters.xmlType ||
                        filters.recognitionStatus ||
                        filters.advanceReport) ? (
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => {
                            handleResetFilters();
                            setPageIndex(0);
                          }}
                        >
                          {t("resetFilters") || "Išvalyti filtrus"}
                        </MDButton>
                      ) : null
                    }
                  >
                    {t("noDocumentsFound")}
                  </Alert>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <DocumentDetailsModal
        open={detailsDialogOpen}
        onClose={closeDetailsDialog}
        selectedDocument={selectedDocument}
        companyId={selectedCompany?.id}
      />

      <PdfExportDialog
        open={pdfDialogOpen}
        onClose={handlePdfDialogClose}
        onConfirm={handlePdfTypeSelect}
        documentType={documentType}
        setDocumentType={setDocumentType}
        unitPriceInput={unitPriceInput}
        setUnitPriceInput={setUnitPriceInput}
      />

      <Dialog
        open={supplierPreflight.open}
        onClose={() => {
          if (!supplierPreflight.loading) {
            setSupplierPreflight({ open: false, loading: false, items: [], exportParams: null });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("missingSupplierSettingsTitle")}</DialogTitle>
        <DialogContent>
          {supplierPreflight.loading ? (
            <MDBox display="flex" alignItems="center" gap={2} py={1}>
              <CircularProgress size={22} />
              <MDTypography variant="body2">{t("checkingSupplierSettings")}</MDTypography>
            </MDBox>
          ) : (
            <>
              <DialogContentText>{t("missingSupplierSettingsDescription")}</DialogContentText>
              <MDBox component="ul" sx={{ pl: 3, mb: 0, maxHeight: 240, overflowY: "auto" }}>
                {supplierPreflight.items.map((item, index) => (
                  <MDBox
                    component="li"
                    key={`${item.supplier_registration || item.supplier_name}-${index}`}
                  >
                    <MDTypography variant="body2">
                      {item.supplier_name}
                      {item.supplier_registration ? ` (${item.supplier_registration})` : ""}
                      {` · ${t("affectedDocumentsCount").replace("{count}", item.document_count)}`}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </>
          )}
        </DialogContent>
        {!supplierPreflight.loading && (
          <DialogActions sx={{ flexWrap: "wrap" }}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() =>
                setSupplierPreflight({ open: false, loading: false, items: [], exportParams: null })
              }
            >
              {t("cancel")}
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => {
                setSupplierPreflight({
                  open: false,
                  loading: false,
                  items: [],
                  exportParams: null,
                });
                const pending = {
                  companyId: selectedCompany?.id,
                  exportParams: supplierPreflight.exportParams,
                };
                sessionStorage.setItem("pendingSupplierExport", JSON.stringify(pending));
                setPendingSupplierExport(pending);
                navigate("/suppliers", {
                  state: { fromExport: true, missingSuppliers: supplierPreflight.items },
                });
              }}
            >
              {t("goToSuppliers")}
            </MDButton>
            <MDButton
              variant="contained"
              color="info"
              onClick={() => {
                const exportParams = supplierPreflight.exportParams;
                setSupplierPreflight({
                  open: false,
                  loading: false,
                  items: [],
                  exportParams: null,
                });
                if (exportParams) continueXmlExport(exportParams);
              }}
            >
              {t("exportAnyway")}
            </MDButton>
          </DialogActions>
        )}
      </Dialog>

      <UnmatchedLineItemsDialog
        open={matchReview.open}
        loading={matchReview.loading}
        items={matchReview.items}
        companyId={matchReview.companyId}
        defaultWarehouseCode={matchReview.defaultWarehouseCode}
        defaultGroup={matchReview.defaultGroup}
        onClose={() => setMatchReview((previous) => ({ ...previous, open: false }))}
        onExport={() => {
          const exportParams = matchReview.exportParams;
          setMatchReview((previous) => ({ ...previous, open: false }));
          if (exportParams) beginXmlExport(exportParams);
        }}
      />

      <Dialog
        open={!!fileExport}
        onClose={exportSaving ? undefined : () => setFileExport(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {fileExport?.type === "excel"
            ? "Excel eksporto nustatymai"
            : "RODA TXT eksporto nustatymai"}
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            Eksportuojamų dokumentų: {fileExport?.documentIds.length || 0}
          </MDTypography>
          {fileExport && (
            <ExportVariantSelector
              companyId={selectedCompany.id}
              value={exportVariant}
              onChange={setExportVariant}
              pvmCodeOptions={pvmCodeOptions}
              disabled={exportSaving}
            />
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" disabled={exportSaving} onClick={() => setFileExport(null)}>
            {t("cancel")}
          </MDButton>
          <MDButton
            color="primary"
            disabled={exportSaving || !isExportVariantValid(exportVariant)}
            onClick={handleFileExportConfirm}
          >
            {exportSaving ? "Priskiriama..." : "Eksportuoti"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <XmlExportSettingsDialog
        companyId={selectedCompany?.id}
        exportVariant={exportVariant}
        setExportVariant={setExportVariant}
        exportSaving={exportSaving}
        open={xmlExportDialogOpen}
        onClose={handleXmlExportDialogClose}
        onConfirm={handleXmlExportConfirm}
        xmlExportType={xmlExportType}
        setXmlExportType={setXmlExportType}
        xmlExportWithMatches={xmlExportWithMatches}
        selectedPozymiai={selectedPozymiai}
        setSelectedPozymiai={setSelectedPozymiai}
        includePvmInTotal={includePvmInTotal}
        setIncludePvmInTotal={setIncludePvmInTotal}
        spacyModelSize={spacyModelSize}
        setSpacyModelSize={setSpacyModelSize}
        matcherType={matcherType}
        setMatcherType={setMatcherType}
        overridePvmCode={overridePvmCode}
        setOverridePvmCode={setOverridePvmCode}
        recalculateVat={recalculateVat}
        setRecalculateVat={setRecalculateVat}
        isBusinessTrip={isBusinessTrip}
        setIsBusinessTrip={setIsBusinessTrip}
        businessTripDocumentNumber={businessTripDocumentNumber}
        setBusinessTripDocumentNumber={setBusinessTripDocumentNumber}
        pvmCodeOptions={pvmCodeOptions}
      />

      {/* Floating Upload Progress */}
      <Snackbar
        open={uploadUi.status !== "idle"}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Card sx={{ p: 2, minWidth: 300, boxShadow: 3 }}>
          {uploadUi.status === "uploading" && (
            <MDBox>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} thickness={5} />
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    {t("uploading") || "Uploading"} {uploadUi.totalFiles || 0}{" "}
                    {t("files") || "files"}
                  </MDTypography>
                </MDBox>
                {typeof uploadUi.progress === "number" && (
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    {uploadUi.progress}%
                  </MDTypography>
                )}
              </MDBox>
              <LinearProgress
                sx={{ mt: 1, borderRadius: 1 }}
                variant={typeof uploadUi.progress === "number" ? "determinate" : "indeterminate"}
                value={typeof uploadUi.progress === "number" ? uploadUi.progress : undefined}
              />
              {uploadUi.files?.length > 0 && (
                <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
                  {uploadUi.files.length > 3
                    ? `${uploadUi.files.slice(0, 3).join(", ")}… (+${uploadUi.files.length - 3})`
                    : uploadUi.files.join(", ")}
                </MDTypography>
              )}
            </MDBox>
          )}

          {uploadUi.status === "success" && uploadUi.message && (
            <Alert
              severity="success"
              onClose={() => setUploadUi((p) => ({ ...p, status: "idle" }))}
              sx={{ width: "100%" }}
            >
              {uploadUi.message}
            </Alert>
          )}

          {uploadUi.status === "error" && (
            <Alert
              severity="warning"
              onClose={() => setUploadUi((p) => ({ ...p, status: "idle" }))}
              sx={{ width: "100%" }}
            >
              {uploadUi.message || t("failedToUploadDocuments") || "Upload finished with issues"}
              {Array.isArray(uploadUi.failedFiles) && uploadUi.failedFiles.length > 0 && (
                <MDBox sx={{ mt: 1 }}>
                  <MDTypography variant="caption" color="text">
                    {t("failedFiles") || "Failed files"}:{" "}
                    {uploadUi.failedFiles.slice(0, 5).join(", ")}
                    {uploadUi.failedFiles.length > 5
                      ? `… (+${uploadUi.failedFiles.length - 5})`
                      : ""}
                  </MDTypography>
                </MDBox>
              )}
            </Alert>
          )}
        </Card>
      </Snackbar>

      <Footer />

      <BulkActionsBar
        selectedCount={selectedDocumentIds.length}
        onClearSelection={() => toggleAllRowsSelected(false)}
        onDelete={() => handleDeleteSelected(selectedDocumentIds)}
        onRecognize={() => handleRecognize(selectedDocumentIds)}
        onExportXml={handleExportXml}
        onExportExcel={handleExportExcel}
        onExportRoda={handleExportRoda}
      />

      <Dialog
        open={saveViewDialogOpen}
        onClose={() => setSaveViewDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("saveView") || "Save view"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t("viewName") || "View name"}
            value={savedViewName}
            onChange={(event) => setSavedViewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveCurrentView();
            }}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setSaveViewDialogOpen(false)}
          >
            {t("cancel")}
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={saveCurrentView}
            disabled={!savedViewName.trim()}
          >
            {t("save")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setDeleteConfirmOpen(false);
          setDocumentsToDelete([]);
          setDeleteCompanyId(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteSelected")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(t("deleteSelectedConfirm") || "Ar tikrai norite ištrinti {count} dokumentų?").replace(
              "{count}",
              documentsToDelete.length
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDocumentsToDelete([]);
              setDeleteCompanyId(null);
            }}
            disabled={deleteMutation.isPending}
          >
            {t("cancel")}
          </MDButton>
          <MDButton
            variant="contained"
            color="error"
            onClick={confirmDeleteSelected}
            disabled={deleteMutation.isPending}
            aria-label={t("delete")}
          >
            {deleteMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t("delete")
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default DocumentPage;
